/**
 * Models and mappers for the Station Assignment History page.
 */
import { SPMSGetShiftPlanResponse } from '../../../types/shiftPlan';

/**
 * A single row in the station assignment history table.
 */
export interface HistoryRow {
  /** OFD date the plan was generated for (raw string from the API). */
  date: string;
  /** Unique plan identifier — passed to the Published screen when opened. */
  planId: string;
  /** Alias/email of the user who created the plan. */
  createdBy: string;
  /** Creation timestamp (raw string/epoch from the API). */
  createdAt: string | number;
}

const PLACEHOLDER = '-';

/**
 * Read the first present value across a list of candidate keys off the
 * loosely-typed shift plan response, coercing to string and falling back to a
 * placeholder when none are present.
 */
const readString = (
  plan: SPMSGetShiftPlanResponse,
  keys: string[]
): string => {
  for (const key of keys) {
    const value = plan[key];
    if (value !== undefined && value !== null && value !== '') {
      return String(value);
    }
  }
  return PLACEHOLDER;
};

/**
 * Read the first present raw value across candidate keys, preserving the
 * original type (string or epoch number) so timestamps can be formatted later.
 */
const readRaw = (
  plan: SPMSGetShiftPlanResponse,
  keys: string[]
): string | number => {
  for (const key of keys) {
    const value = plan[key];
    if (value !== undefined && value !== null && value !== '' && typeof value !== 'boolean') {
      return value;
    }
  }
  return PLACEHOLDER;
};

/**
 * Map the raw `getAllShiftPlans` response entries into table rows. Only plans
 * with a usable `planId` are kept, since the Open link relies on it. Field
 * names are read defensively across the aliases the SPMS response is known to
 * use (`createdAt`/`createdDate`).
 */
export const mapShiftPlansToHistoryRows = (
  shiftPlans: SPMSGetShiftPlanResponse[] | undefined | null
): HistoryRow[] => {
  if (!shiftPlans || !Array.isArray(shiftPlans)) {
    return [];
  }

  return shiftPlans
    .filter((plan) => plan.planId !== undefined && plan.planId !== null && plan.planId !== '')
    .map((plan) => ({
      date: readString(plan, ['ofdDate']),
      planId: String(plan.planId),
      createdBy: readString(plan, ['createdBy']),
      createdAt: readRaw(plan, ['createdAt', 'createdDate']),
    }));
};
